/**
 * Accessibility Hooks for React Components
 * Provides hooks for focus management, keyboard navigation, and screen reader support
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { 
  FocusManager, 
  ScreenReader, 
  KEYBOARD_HELPERS, 
  KEYBOARD_KEYS,
  ARIA_HELPERS 
} from '../utils/accessibility';

// Focus Management Hook
export function useFocusManagement() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFocusTrapped, setIsFocusTrapped] = useState(false);

  const trapFocus = useCallback((event: KeyboardEvent) => {
    if (containerRef.current && isFocusTrapped) {
      FocusManager.trapFocus(containerRef.current, event);
    }
  }, [isFocusTrapped]);

  const focusFirstElement = useCallback(() => {
    if (containerRef.current) {
      FocusManager.focusFirstElement(containerRef.current);
    }
  }, []);

  const focusLastElement = useCallback(() => {
    if (containerRef.current) {
      FocusManager.focusLastElement(containerRef.current);
    }
  }, []);

  const storeFocus = useCallback(() => {
    return FocusManager.storeFocus();
  }, []);

  const restoreFocus = useCallback((element: HTMLElement | null) => {
    FocusManager.restoreFocus(element);
  }, []);

  useEffect(() => {
    if (isFocusTrapped) {
      document.addEventListener('keydown', trapFocus);
      return () => document.removeEventListener('keydown', trapFocus);
    }
  }, [isFocusTrapped, trapFocus]);

  return {
    containerRef,
    isFocusTrapped,
    setIsFocusTrapped,
    focusFirstElement,
    focusLastElement,
    storeFocus,
    restoreFocus
  };
}

// Keyboard Navigation Hook
export function useKeyboardNavigation<T>(
  items: T[],
  onSelect: (item: T, index: number) => void,
  initialIndex: number = 0
) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const handled = KEYBOARD_HELPERS.handleArrowNavigation(
      event,
      currentIndex,
      items.length,
      (newIndex) => {
        setCurrentIndex(newIndex);
        onSelect(items[newIndex], newIndex);
      }
    );

    if (!handled) {
      // Handle activation keys
      KEYBOARD_HELPERS.handleActivation(event, () => {
        onSelect(items[currentIndex], currentIndex);
      });
    }
  }, [currentIndex, items, onSelect]);

  const navigateToIndex = useCallback((index: number) => {
    if (index >= 0 && index < items.length) {
      setCurrentIndex(index);
      onSelect(items[index], index);
    }
  }, [items, onSelect]);

  const navigateNext = useCallback(() => {
    const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
    navigateToIndex(nextIndex);
  }, [currentIndex, items.length, navigateToIndex]);

  const navigatePrevious = useCallback(() => {
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
    navigateToIndex(prevIndex);
  }, [currentIndex, items.length, navigateToIndex]);

  return {
    currentIndex,
    setCurrentIndex,
    handleKeyDown,
    navigateToIndex,
    navigateNext,
    navigatePrevious
  };
}

// Screen Reader Hook
export function useScreenReader() {
  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    ScreenReader.announce(message, priority);
  }, []);

  const announceLoading = useCallback((message: string = 'Loading') => {
    ScreenReader.announceLoading(message);
  }, []);

  const announceSuccess = useCallback((message: string) => {
    ScreenReader.announceSuccess(message);
  }, []);

  const announceError = useCallback((message: string) => {
    ScreenReader.announceError(message);
  }, []);

  const announceValidationErrors = useCallback((errors: string[]) => {
    ScreenReader.announceValidationErrors(errors);
  }, []);

  return {
    announce,
    announceLoading,
    announceSuccess,
    announceError,
    announceValidationErrors
  };
}

// ARIA Hook
export function useAria() {
  const generateId = useCallback((prefix: string = 'aria') => {
    return ARIA_HELPERS.generateId(prefix);
  }, []);

  const createDescribedBy = useCallback((element: HTMLElement, description: string) => {
    ARIA_HELPERS.createDescribedBy(element, description);
  }, []);

  const createLabelledBy = useCallback((element: HTMLElement, label: string) => {
    ARIA_HELPERS.createLabelledBy(element, label);
  }, []);

  const setLiveRegion = useCallback((element: HTMLElement, priority: 'polite' | 'assertive' = 'polite') => {
    ARIA_HELPERS.setLiveRegion(element, priority);
  }, []);

  return {
    generateId,
    createDescribedBy,
    createLabelledBy,
    setLiveRegion
  };
}

// Modal Accessibility Hook
export function useModalAccessibility(
  isOpen: boolean,
  onClose: () => void,
  title: string
) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const { setIsFocusTrapped, focusFirstElement, restoreFocus } = useFocusManagement();
  const { announce } = useScreenReader();

  // Handle escape key
  const handleEscape = useCallback((event: KeyboardEvent) => {
    KEYBOARD_HELPERS.handleEscape(event, onClose);
  }, [onClose]);

  // Set up modal when opened
  useEffect(() => {
    if (isOpen) {
      // Store current focus
      previousFocus.current = document.activeElement as HTMLElement;
      
      // Trap focus
      setIsFocusTrapped(true);
      
      // Announce modal opening
      announce(`Modal opened: ${title}`, 'assertive');
      
      // Add escape key listener
      document.addEventListener('keydown', handleEscape);
      
      // Focus first element after a brief delay
      setTimeout(() => {
        focusFirstElement();
      }, 100);
    } else {
      // Restore focus when modal closes
      if (previousFocus.current) {
        restoreFocus(previousFocus.current);
      }
      
      // Remove focus trap
      setIsFocusTrapped(false);
      
      // Remove escape key listener
      document.removeEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, title, announce, setIsFocusTrapped, focusFirstElement, restoreFocus, handleEscape]);

  return {
    modalRef,
    previousFocus: previousFocus.current
  };
}

// List Accessibility Hook
export function useListAccessibility<T>(
  items: T[],
  onSelect: (item: T, index: number) => void,
  getItemLabel: (item: T) => string
) {
  const listRef = useRef<HTMLUListElement>(null);
  const { currentIndex, handleKeyDown, navigateToIndex } = useKeyboardNavigation(
    items,
    onSelect
  );
  const { announce } = useScreenReader();

  const handleItemSelect = useCallback((item: T, index: number) => {
    onSelect(item, index);
    announce(`Selected: ${getItemLabel(item)}`);
  }, [onSelect, getItemLabel, announce]);

  const handleItemFocus = useCallback((item: T, index: number) => {
    announce(`${getItemLabel(item)}, item ${index + 1} of ${items.length}`);
  }, [getItemLabel, items.length, announce]);

  return {
    listRef,
    currentIndex,
    handleKeyDown,
    navigateToIndex,
    handleItemSelect,
    handleItemFocus
  };
}

// Form Accessibility Hook
export function useFormAccessibility() {
  const { announceError, announceSuccess } = useScreenReader();
  const [errors, setErrors] = useState<string[]>([]);

  const announceValidationErrors = useCallback((validationErrors: string[]) => {
    setErrors(validationErrors);
    announceError(`Form has ${validationErrors.length} validation errors`);
  }, [announceError]);

  const announceFormSuccess = useCallback((message: string) => {
    announceSuccess(message);
  }, [announceSuccess]);

  const announceFieldError = useCallback((fieldName: string, error: string) => {
    announceError(`${fieldName}: ${error}`);
  }, [announceError]);

  const announceFieldSuccess = useCallback((fieldName: string) => {
    announceSuccess(`${fieldName} is valid`);
  }, [announceSuccess]);

  return {
    errors,
    announceValidationErrors,
    announceFormSuccess,
    announceFieldError,
    announceFieldSuccess
  };
}

// Skip Link Hook
export function useSkipLinks() {
  const { generateId } = useAria();
  const [skipTargets, setSkipTargets] = useState<Map<string, HTMLElement>>(new Map());

  const registerSkipTarget = useCallback((id: string, element: HTMLElement) => {
    setSkipTargets(prev => new Map(prev).set(id, element));
  }, []);

  const unregisterSkipTarget = useCallback((id: string) => {
    setSkipTargets(prev => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });
  }, []);

  const focusSkipTarget = useCallback((id: string) => {
    const element = skipTargets.get(id);
    if (element) {
      element.focus();
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [skipTargets]);

  return {
    skipTargets,
    registerSkipTarget,
    unregisterSkipTarget,
    focusSkipTarget,
    generateId
  };
}

// Live Region Hook
export function useLiveRegion(priority: 'polite' | 'assertive' = 'polite') {
  const regionRef = useRef<HTMLDivElement>(null);
  const { setLiveRegion } = useAria();

  useEffect(() => {
    if (regionRef.current) {
      setLiveRegion(regionRef.current, priority);
    }
  }, [priority, setLiveRegion]);

  const announce = useCallback((message: string) => {
    if (regionRef.current) {
      regionRef.current.textContent = message;
    }
  }, []);

  return {
    regionRef,
    announce
  };
}

export default {
  useFocusManagement,
  useKeyboardNavigation,
  useScreenReader,
  useAria,
  useModalAccessibility,
  useListAccessibility,
  useFormAccessibility,
  useSkipLinks,
  useLiveRegion
};
